import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, Upload, X, Check, Loader2, Wand2 } from "lucide-react";
import {
  aiGetBrandKit, aiSaveBrandKit, aiUploadBrandKitLogo, aiDeleteBrandKitLogo, aiBrandKitLogoUrl,
} from "@/lib/gfApi";

const SWATCHES = ["#D4AF37", "#0F172A", "#EC4899", "#8B5CF6", "#22D3EE", "#F97316", "#10B981", "#EF4444"];

export const BrandKitCard = ({ email, onChange }) => {
  const [open, setOpen] = useState(false);
  const [bk, setBk] = useState(null);
  const [accent, setAccent] = useState("");
  const [tagline, setTagline] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoBust, setLogoBust] = useState(0);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const d = await aiGetBrandKit(email);
      setBk(d);
      setAccent(d.accent || "");
      setTagline(d.tagline || "");
    } catch { /* silent */ }
  };
  useEffect(() => { if (email) refresh(); /* eslint-disable-next-line */ }, [email]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await aiSaveBrandKit({ email, accent, tagline });
      setBk(d);
      toast.success("Brand Kit disimpan.");
      onChange?.(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan Brand Kit.");
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("email", email);
    fd.append("file", f);
    try {
      await aiUploadBrandKitLogo(fd);
      await refresh();
      setLogoBust(Date.now());
      toast.success("Logo Brand Kit dikemas kini.");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat naik logo.");
    }
  };

  const removeLogo = async () => {
    try {
      await aiDeleteBrandKitLogo(email);
      await refresh();
      onChange?.();
    } catch { toast.error("Gagal memadam logo."); }
  };

  return (
    <div className="mb-4 rounded-2xl border border-[#E7E2D6] bg-white" data-testid="brand-kit-card">
      <button
        data-testid="brand-kit-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3.5 text-left"
      >
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFFBEF]">
            <Palette className="h-4 w-4 text-[#B8912B]" />
          </span>
          <span>
            <span className="block text-sm font-bold text-[#0F172A]">Brand Kit</span>
            <span className="block text-xs text-slate-400">Logo & warna jenama — simpan sekali, guna berkali-kali</span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          {bk?.accent && <span className="h-4 w-4 rounded-full border border-[#E7E2D6]" style={{ background: bk.accent }} />}
          <span className="text-xs font-semibold text-[#B8912B]">{open ? "Tutup" : "Urus"}</span>
        </span>
      </button>

      {open && (
        <div className="border-t border-[#F1ECE0] px-4 py-4" data-testid="brand-kit-body">
          {/* Logo */}
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Logo jenama</p>
          {bk?.has_logo ? (
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-[#E7E2D6] p-3" data-testid="brand-kit-logo-preview">
              <img src={`${aiBrandKitLogoUrl(email)}&v=${logoBust}`} alt="logo" className="h-14 w-14 rounded-lg object-contain" />
              <span className="flex-1 text-xs text-slate-500">Logo tersimpan</span>
              <button onClick={() => fileRef.current?.click()} className="rounded-full border border-[#D4AF37] px-3 py-1.5 text-xs font-bold text-[#B8912B]" data-testid="brand-kit-logo-replace">Tukar</button>
              <button onClick={removeLogo} data-testid="brand-kit-logo-remove" className="rounded-full bg-rose-500 p-1.5 text-white"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} data-testid="brand-kit-logo-upload" className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#0F172A]/20 py-4 text-sm font-semibold text-slate-500">
              <Upload className="h-4 w-4" /> Muat naik logo (PNG telus paling elok)
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={onLogo} data-testid="brand-kit-logo-input" />

          {/* Accent colour */}
          <p className="mt-5 text-xs font-bold uppercase tracking-wide text-slate-500">Warna aksen poster</p>
          <p className="mt-0.5 text-[11px] text-slate-400">Menggantikan warna lalai archetype untuk konsistensi jenama.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="brand-kit-swatches">
            {SWATCHES.map((s) => (
              <button
                key={s}
                data-testid={`brand-kit-swatch-${s.replace('#', '')}`}
                onClick={() => setAccent(s)}
                className={`h-8 w-8 rounded-full border-2 transition-transform ${accent.toLowerCase() === s.toLowerCase() ? "border-[#0F172A] scale-110" : "border-transparent"}`}
                style={{ background: s }}
              >
                {accent.toLowerCase() === s.toLowerCase() && <Check className="mx-auto h-4 w-4 text-white" />}
              </button>
            ))}
            <label className="flex items-center gap-1.5 rounded-full border border-[#E7E2D6] px-2.5 py-1.5">
              <input type="color" value={accent || "#D4AF37"} onChange={(e) => setAccent(e.target.value)} className="h-5 w-5 cursor-pointer border-0 bg-transparent p-0" data-testid="brand-kit-color-input" />
              <span className="text-xs text-slate-500">{accent || "Custom"}</span>
            </label>
            {accent && (
              <button onClick={() => setAccent("")} className="text-xs text-slate-400 underline" data-testid="brand-kit-accent-clear">Reset</button>
            )}
          </div>

          {/* Default tagline */}
          <p className="mt-5 text-xs font-bold uppercase tracking-wide text-slate-500">Tagline lalai</p>
          <input
            data-testid="brand-kit-tagline-input"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="cth: Rasa sebenar, setiap hari"
            className="mt-2 w-full rounded-xl border border-[#E7E2D6] px-3.5 py-2.5 text-sm outline-none focus:border-[#D4AF37]"
          />
          <p className="mt-1 text-[11px] text-slate-400">Maksimum 8 patah perkataan. Akan mengisi tagline kempen baharu secara automatik.</p>

          <button onClick={save} disabled={saving} data-testid="brand-kit-save" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Simpan Brand Kit
          </button>
        </div>
      )}
    </div>
  );
};
